import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ArrowLeft, Sparkles, Loader2 } from "lucide-react";
import nextIcon from "../../assets/nextButtonIcon.png";
import apiClient from "../../api/apiClient";
import { ENDPOINTS } from "../../api/endPoints";
import { useGlobalAlert } from "../../context/AlertContext";
import appIcon from "../../assets/appIcon.svg";
import MobileTopBar from "../../components/MobileTopBar";
import MobileBottomBar from "../../components/MobileBottomBar";
import DesktopTopBar from "../../components/DesktopTopBar";

const WriteDogBioEditPage = () => {
  const navigate = useNavigate();
  const { triggerAlert } = useGlobalAlert();

  const { profile } = useSelector((state) => state.user);

  const [user, setUser] = useState(null);
  const [userDog, setUserDog] = useState(null);
  const [formData, setFormData] = useState({
    dogId: "",
    dogName: "",
    bio: "",
  });

  const dogName = formData?.dogName || "Dog Name";

  const [bioText, setBioText] = useState("");
  const [loadingBio, setLoadingBio] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [loading, setLoading] = useState(true);

  const isBioValid = bioText.trim().length > 0;

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const res = await apiClient.get(`/user/details/${profile?.id}`);
        if (res.data?.status && res.data.data) {
          const userData = res.data.data;
          setUser(userData);

          if (userData.dogs && userData.dogs.length > 0) {
            const dog = userData.dogs[0];
            setUserDog(dog);

            setFormData({
              dogId: dog.id,
              dogName: dog.dog_name || "Dog",
              bio: dog.bio || "",
            });

            setBioText(dog.bio || "");
          }
        }
      } catch (err) {
        console.error("Error fetching details:", err);
        triggerAlert("Failed to load profile details.", "error");
      } finally {
        setLoading(false);
      }
    };

    if (profile?.id) fetchDetails();
  }, [profile?.id, triggerAlert]);

  const handleGenerateAIBio = async () => {
    setLoadingBio(true);
    try {
      let breedPayload = userDog?.dog_breed;

      if (
        userDog?.dog_breed === "Mixed Breed" &&
        Array.isArray(userDog?.dog_mixedBreed)
      ) {
        breedPayload = userDog.dog_mixedBreed.join(", ");
      }

      const payload = {
        dog_name: formData?.dogName || "Dog",
        dog_gender: userDog?.dog_gender || "Not Specified",
        dog_breed: breedPayload || "Unknown",
        personality: userDog?.personality || [],
      };

      const response = await apiClient.post(ENDPOINTS.GENERATE_AI_BIO, payload);

      if (response.data?.success && response.data?.AI_generated_bio) {
        setBioText(response.data.AI_generated_bio);
        triggerAlert("New bio generated successfully!", "success");
      } else {
        triggerAlert("Could not generate bio at this time.", "error");
      }
    } catch (error) {
      console.error("AI Bio Error:", error.response?.data || error.message);
      triggerAlert("Failed to generate bio. Please try again.", "error");
    } finally {
      setLoadingBio(false);
    }
  };

  const handleNext = async () => {
    if (!bioText.trim() || isUpdating) return;
    setIsUpdating(true);

    try {
      const dogId = formData.dogId;
      if (!dogId) {
        triggerAlert("Could not identify the dog to update.", "error");
        setIsUpdating(false);
        return;
      }

      const apiPayload = {
        user_id: profile?.id,
        bio: bioText,
      };

      const response = await apiClient.put(
        `/dogs/update-dog-by-id/${dogId}`,
        apiPayload,
      );

      if (response.data?.status) {
        triggerAlert("Bio updated successfully!", "success");
        navigate("/user-profile");
      } else {
        triggerAlert("Failed to save the bio.", "error");
      }
    } catch (error) {
      console.error("Update failed:", error);
      triggerAlert("An error occurred while saving.", "error");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleExit = () => {
    navigate("/user-profile");
  };

  if (loading) {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background:
            "linear-gradient(139.27deg, #009fd9 23.13%, #005b7d 98.96%)",
          zIndex: 9999,
        }}
      >
        <Loader2 className="animate-spin" size={40} color="#000" />
      </div>
    );
  }

  return (
    <div className="iyow-container">
      {/* Header */}
      {/* <header className="iyow-header desktop-only"> */}
      {/* <button
          className="iyow-back-btn"
          onClick={() => navigate("/edit-exp-with-humans")}
        >
          <ArrowLeft size={22} color="#fff" strokeWidth={3} />
          <span>Back</span>
        </button> */}
      {/* <div className="iyow-header-logo">
          <img src={appIcon} alt="Social Paws" className="h-logo-img" />
          <span className="iyow-logo-text">Social Paws</span>
        </div> */}
      {/* </header> */}
      <DesktopTopBar
        showBack={true}
        onBack={() => navigate("/edit-exp-with-humans")}
        backColor="#fff"
      />

      <MobileTopBar
        onBack={() => navigate("/edit-exp-with-humans")}
        backColor="#fff"
        logoColor="#fff"
      />

      {/* Main Content */}
      <main className="iyow-main-content">
        <h1 className="iyow-title">IN YOUR OWN WORDS</h1>
        <p className="iyow-subtitle">
          Share more about what brings you to Social Paws, {dogName}'s greatest
          traits, and whatever else your heart desires!
        </p>

        <div className="iyow-input-wrapper">
          <textarea
            className="iyow-textarea"
            placeholder="Write here"
            value={bioText}
            onChange={(e) => setBioText(e.target.value)}
            disabled={isUpdating}
          />
          <button
            type="button"
            className="iyow-ai-btn"
            onClick={handleGenerateAIBio}
            disabled={loadingBio || isUpdating}
          >
            {loadingBio ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <Sparkles size={18} strokeWidth={2} />
            )}
            {loadingBio
              ? "GENERATING..."
              : `WRITE ${dogName?.toUpperCase()}'S BIO`}
          </button>
          <p
            className="iyow-ai-help-text"
            style={{ marginTop: window.innerWidth >= 1024 ? "" : "-1rem" }}
          >
            Want a helping paw?
            <br />
            Let AI write the bio!
            <br />
            you can edit before saving
          </p>
        </div>
      </main>

      {/* Footer */}
      <div className="paws-exp-footer-h desktop-only">
        <button
          className="paws-skip-btn-h"
          onClick={handleExit}
          disabled={isUpdating}
        >
          EXIT
        </button>

        <button
          className={`iyow-next-btn ${isBioValid && !isUpdating ? "enabled" : "disabled"}`}
          onClick={handleNext}
          disabled={!isBioValid || isUpdating}
        >
          <span className="paws-next-text-h">
            {isUpdating ? "SAVING..." : "FINISH"}
          </span>
          <div className="paws-next-circle-h">
            {isUpdating ? (
              <Loader2
                className="animate-spin"
                size={20}
                color="#fff"
                style={{ margin: "auto" }}
              />
            ) : (
              <img
                src={nextIcon}
                alt="Next Icon"
                style={{
                  width: "140%",
                  height: "140%",
                  objectFit: "contain",
                  opacity: isBioValid ? 1 : 0.5,
                }}
              />
            )}
          </div>
        </button>
      </div>

      <div className="paws-exp-footer-h mobile-only">
        <MobileBottomBar
          onNext={handleNext}
          isNextEnabled={isBioValid && !isUpdating}
          nextIconSrc={nextIcon}
          showSkip={true}
          skipText="EXIT"
          nextText="FINISH"
          onSkip={handleExit}
          variant="green"
        />
      </div>
    </div>
  );
};

export default WriteDogBioEditPage;
